import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, FileText, Calendar, Palette, Brain, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import DashboardShell from "@/pages/dashboard/DashboardShell";
import { useAuth } from "@/lib/auth";
import {
  ARCHETYPE_LABELS,
  ARCHETYPE_BLURBS,
  ARCHETYPES,
  listProjects,
  deleteProject,
} from "@/lib/vault";
import { extractFormError } from "@/lib/api";

const ARCHETYPE_ICONS = {
  lead_capture: FileText,
  content_pack: Calendar,
  brand_kit: Palette,
  recommender: Brain,
};

const STATUS_STYLE = {
  draft: "text-neutral-500 border-neutral-200",
  published: "text-emerald-300 border-emerald-400/40",
  archived: "text-neutral-400 border-neutral-200",
};

function ProjectCard({ project, onDelete }) {
  const Icon = ARCHETYPE_ICONS[project.archetype] || FileText;
  return (
    <div
      data-testid={`vault-project-${project.id}`}
      className="group border border-neutral-200 bg-white p-6 sm:p-7 hover:border-gold/40 transition flex flex-col"
    >
      <div className="flex items-start justify-between gap-3">
        <Icon size={20} className="text-gold" strokeWidth={1.4} />
        <span
          className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 border ${STATUS_STYLE[project.status] || STATUS_STYLE.draft}`}
        >
          {project.status}
        </span>
      </div>
      <h3 className="font-serif text-xl mt-5 leading-snug">{project.name}</h3>
      <p className="text-xs uppercase tracking-[0.18em] text-neutral-400 mt-2">
        {ARCHETYPE_LABELS[project.archetype] || project.archetype}
      </p>

      <div className="flex items-center gap-3 mt-6 pt-5 border-t border-neutral-200/70">
        <Link
          to={`/dashboard/vault/${project.id}`}
          data-testid={`vault-open-${project.id}`}
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-gold hover:text-gold transition"
        >
          Open <ExternalLink size={12} />
        </Link>
        <button
          type="button"
          onClick={() => onDelete(project)}
          data-testid={`vault-delete-${project.id}`}
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-neutral-400 hover:text-rose-300 transition ml-auto"
        >
          <Trash2 size={12} /> Delete
        </button>
      </div>
    </div>
  );
}

function EmptyState({ isPending }) {
  return (
    <div
      data-testid="vault-empty"
      className="border border-dashed border-neutral-200 p-10 sm:p-16 text-center"
    >
      <p className="overline">Your Vault is quiet</p>
      <h2 className="font-serif text-3xl sm:text-4xl mt-4 leading-snug">
        Build your first project with the{" "}
        <span className="italic text-aurora">Studio Builder.</span>
      </h2>
      <p className="text-neutral-500 mt-4 max-w-xl mx-auto leading-relaxed">
        Describe what you need in plain English. The Studio drafts a lead form, content pack,
        brand kit or recommender quiz, ready to publish.
      </p>
      <Link
        to="/dashboard/builder"
        data-testid="vault-empty-cta"
        className={`btn-aurora justify-center mt-8 inline-flex ${isPending ? "opacity-60 pointer-events-none" : ""}`}
        aria-disabled={isPending}
      >
        <Plus size={14} /> Open the Studio Builder
      </Link>
      {isPending && (
        <p className="text-xs text-neutral-400 mt-5">
          Pick a plan first.{" "}
          <Link to="/pricing" className="text-gold underline-offset-4 hover:underline">
            See pricing
          </Link>
          .
        </p>
      )}
    </div>
  );
}

export default function VaultPage() {
  const { user } = useAuth();
  const [projects, setProjects] = useState(null);
  const isPending = (user?.plan || "pending") === "pending" && user?.role !== "admin";

  useEffect(() => {
    let cancelled = false;
    listProjects()
      .then((data) => {
        if (!cancelled) setProjects(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setProjects([]);
          toast.error(extractFormError(err, "Couldn't load your Vault."));
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleDelete = async (project) => {
    if (typeof window !== "undefined" && !window.confirm(`Delete "${project.name}"? This cannot be undone.`)) return;
    try {
      await deleteProject(project.id);
      setProjects((curr) => (curr || []).filter((p) => p.id !== project.id));
      toast.success(`${project.name} deleted.`);
    } catch (err) {
      toast.error(extractFormError(err, "Couldn't delete that project."));
    }
  };

  return (
    <DashboardShell back="/dashboard" backLabel="Dashboard">
      <section data-testid="vault-hero" className="flex items-end justify-between flex-wrap gap-6">
        <div>
          <p className="overline">GoldKey Vault</p>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-[1.05] mt-4">
            Your <span className="italic text-aurora">projects.</span>
          </h1>
          <p className="text-neutral-500 mt-4 max-w-xl leading-relaxed">
            Every Studio Builder draft lives here. Open one to preview, edit, publish or share.
          </p>
        </div>
        <Link
          to="/dashboard/builder"
          data-testid="vault-new-cta"
          className={`btn-aurora ${isPending ? "opacity-60 pointer-events-none" : ""}`}
          aria-disabled={isPending}
        >
          <Plus size={14} /> New project
        </Link>
      </section>

      {isPending && (
        <div
          data-testid="vault-paywall"
          className="mt-10 border border-gold/30 bg-gold/[0.04] p-6 sm:p-7"
        >
          <p className="overline">Pick a plan to start building</p>
          <p className="text-neutral-600 mt-3 leading-relaxed max-w-2xl">
            The Vault and Studio Builder activate the moment your plan does. Starter is{" "}
            <span className="text-aurora-static font-medium">$49/mo</span>, Growth is{" "}
            <span className="text-aurora-static font-medium">$199/mo</span>. Cancel any time.
          </p>
          <Link
            to="/pricing"
            data-testid="vault-paywall-cta"
            className="btn-ghost mt-5 inline-flex"
          >
            See plans
          </Link>
        </div>
      )}

      <section className="mt-10 sm:mt-12">
        {projects === null && (
          <div className="border border-neutral-200/70 bg-white p-8 text-neutral-500 text-sm" data-testid="vault-loading">
            Loading your Vault...
          </div>
        )}
        {projects && projects.length === 0 && <EmptyState isPending={isPending} />}
        {projects && projects.length > 0 && (
          <div
            data-testid="vault-grid"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6"
          >
            {projects.map((p) => (
              <ProjectCard key={p.id} project={p} onDelete={handleDelete} />
            ))}
          </div>
        )}
      </section>

      <section className="mt-16 border-t border-neutral-200/70 pt-10">
        <p className="overline">What can I build?</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 mt-6">
          {ARCHETYPES.map((a) => {
            const Icon = ARCHETYPE_ICONS[a];
            return (
              <div
                key={a}
                data-testid={`vault-archetype-${a}`}
                className="border border-neutral-200 bg-white p-5 sm:p-6 hover:border-gold/30 transition"
              >
                <Icon size={18} className="text-gold" strokeWidth={1.4} />
                <h3 className="font-serif text-lg mt-4">{ARCHETYPE_LABELS[a]}</h3>
                <p className="text-xs text-neutral-500 mt-2 leading-relaxed">
                  {ARCHETYPE_BLURBS[a]}
                </p>
              </div>
            );
          })}
        </div>
      </section>
    </DashboardShell>
  );
}
